import { useParams } from "react-router-dom"
import { Card } from "../components/ui/card"
import { Spinner } from "../components/ui/spinner"
import { StatusPill } from "../components/ui/status-pill"
import { ApiError } from "../lib/api"
import { useSharedRun, useSharedRunMeta } from "../lib/hooks"
import { isSafeHttpUrl } from "../lib/url"

/**
 * Phase 6 Stage C-D: 共有 URL ページ (auth 不要、share_token 経由で公開)。
 *
 * 公開範囲 (user 確定 2026-06-10 = A): result.json の candidate 一覧 + source video info のみ。
 * 評価データ (rating / comment) は所有者のみで、ここには表示しない。
 */
export function SharedRun() {
  const { token } = useParams<{ token: string }>()
  const result = useSharedRun(token)
  const meta = useSharedRunMeta(token)

  if (result.isLoading || meta.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Spinner className="size-6" />
      </div>
    )
  }

  if (result.isError) {
    const status = result.error instanceof ApiError ? result.error.status : null
    const msg =
      status === 404
        ? "この共有リンクは見つかりません (解除されたか、URL が間違っています)"
        : result.error instanceof Error
          ? result.error.message
          : "読み込みに失敗しました"
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-8">
        <Card className="flex max-w-md flex-col gap-4 p-8">
          <h2 className="text-lg font-semibold text-text-primary">共有リンクを表示できません</h2>
          <p className="text-sm text-text-secondary">{msg}</p>
        </Card>
      </div>
    )
  }

  const data = result.data
  if (!data) {
    return null
  }

  const source = data.source ?? {}
  const candidates = data.candidates ?? []
  const runStatus = data.run?.status ?? meta.data?.status ?? "unknown"

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-border bg-surface px-6 py-3">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <h1 className="text-base font-semibold text-text-primary">OMC 共有ビュー (read-only)</h1>
          <StatusPill state={runStatus} />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-6">
        <Card className="mb-6 flex flex-col gap-3 p-5">
          <div>
            <div className="text-xs text-text-secondary">動画</div>
            <div className="text-sm font-medium text-text-primary">
              {source.title || "(タイトル不明)"}
            </div>
            {source.url &&
              (isSafeHttpUrl(source.url) ? (
                // http/https のみクリック可能。`javascript:`/`data:` 等の unsafe scheme は
                // テキストのみ表示して click-based XSS を防ぐ (anon 公開経路なので XSS の影響大)。
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-accent hover:underline"
                >
                  {source.url}
                </a>
              ) : (
                <span className="text-xs text-text-muted">{source.url}</span>
              ))}
          </div>
          {source.channel && (
            <div>
              <div className="text-xs text-text-secondary">チャンネル</div>
              <div className="text-sm text-text-primary">{source.channel}</div>
            </div>
          )}
        </Card>

        <h2 className="mb-3 text-sm font-semibold text-text-primary">
          候補一覧 ({candidates.length})
        </h2>
        <div className="flex flex-col gap-3">
          {candidates.length === 0 && (
            <Card className="p-5 text-sm text-text-secondary">候補がありません</Card>
          )}
          {candidates.map((c, i) => (
            // key は start_sec/end_sec ベースだと同一 timestamp の候補で重複し得るので、
            // rank (run 内 unique) or i に fallback する。
            <Card key={`rank-${c.rank ?? i}`} className="flex flex-col gap-2 p-4">
              <div className="flex items-baseline justify-between">
                <div className="text-sm font-medium text-text-primary">#{c.rank ?? i + 1}</div>
                {typeof c.primary_score === "number" && (
                  <div className="text-xs text-text-secondary">
                    score: {c.primary_score.toFixed(2)}
                  </div>
                )}
              </div>
              <div className="text-xs text-text-secondary">
                {fmtTime(c.start_sec)} - {fmtTime(c.end_sec)} ({fmtDuration(c.start_sec, c.end_sec)}
                )
              </div>
              {c.preview_text && (
                <div className="text-xs text-text-secondary">{c.preview_text}</div>
              )}
            </Card>
          ))}
        </div>
      </main>
    </div>
  )
}

function fmtTime(sec: number | null | undefined): string {
  if (sec == null) return "--:--"
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

function fmtDuration(start: number | null | undefined, end: number | null | undefined): string {
  // 両方が finite number のときだけ duration を出す。片方欠けた場合 0 や負数を表示しない。
  if (
    typeof start !== "number" ||
    typeof end !== "number" ||
    !Number.isFinite(start) ||
    !Number.isFinite(end)
  ) {
    return "-"
  }
  return `${Math.round(end - start)}s`
}
